/**
 * Shared default parameters for Clients queries
 * 
 * These constants ensure that the bootstrap prefetch uses the exact same
 * query key as the initial query in the Clients page, so cached data is
 * immediately available on first visit.
 * 
 * PAGINATION STRATEGY:
 * - We use server-side pagination with a reasonable default page size (25)
 * - Salesforce-style: 25 clients per page for fast initial load
 * - The Clients page supports pagination UI to navigate between pages
 * - Only the first page is prefetched on app mount (lightweight)
 * - This is scalable for multi-tenant CRM with thousands of clients per company
 */

import { ClientsQueryParams } from './useClientsQuery';

/**
 * Default page number for Clients queries
 */
export const DEFAULT_CLIENTS_PAGE = 1;

/**
 * Default page size for Clients queries
 * 
 * A reasonable default that balances:
 * - Performance (not too many records per request)
 * - User experience (enough records to be useful)
 * - Scalability (works for companies with 5000+ clients)
 * 
 * SALESFORCE-STYLE: Default 25 per page for fast initial load
 * Users can change this via the pagination UI in the Clients page.
 */
export const DEFAULT_CLIENTS_PAGE_SIZE = 25;

/**
 * Default parameters for the main Clients list query (used in Clients.tsx)
 * 
 * IMPORTANT: These params must match exactly what useClientsQuery passes to clientKeys.list()
 * to ensure the query keys match and cached data is immediately available.
 * 
 * Note: status and select are explicitly undefined to match useClientsQuery behavior
 * (useClientsQuery destructures these and passes them to the query key even if undefined)
 */
export const DEFAULT_CLIENTS_QUERY_PARAMS: ClientsQueryParams = {
  page: DEFAULT_CLIENTS_PAGE,
  limit: DEFAULT_CLIENTS_PAGE_SIZE, // Reasonable page size for scalable pagination
  search: '', // Empty string - no search filter
  status: undefined, // Explicitly undefined to match useClientsQuery query key structure
  select: undefined, // Explicitly undefined to match useClientsQuery query key structure
};

